/*
 * Copyright (c) 2022
 *
 * Created Date: Tuesday, September 20th 2022, 1:35:46 pm
 * Author: Muhammad Fajariansyah Ismail
 *
 */

import "./App.css";


import {lazy, Suspense} from "react";

import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import { Provider } from "jotai";
import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react'; // Provider imports 'rollbar'

import { ADMIN_ROUTE_PREFIX, APP_ROUTE_PREFIX, SUPER_ADMIN_ROUTE_PREFIX, TRAINER_ROUTE_PREFIX } from "routes/RoutePrefix";
import TrainerRouteConstant from "./constant/TrainerRouteConstant";
import RouteConstant from "routes/constant/RouteConstant";
import RollbarConfig from "utils/RollbarConfig";
import useNetworkHook from "hooks/useNetworkHook";

import ClientError from 'pages/ClientError'
import AuthPage from "pages/Auth";
import PreloadPage, { LoginPreload } from "components/Common/PreloadPage";

const AppRedirect = lazy(()=> import("pages/AppRedirect/AppRedirect"));
const ChooseProfilePage = lazy(()=> import("pages/ChooseProfile"));
const PrintQrCode = lazy(()=> import("pages/Trainer/ViewParticipant/PrintQrCode"));
const OfflineCourseScan = lazy(() => import("pages/OfflineCourseScan"));

const SuperAdminAppTemplate = lazy(() => import("routes/SuperAdminAppTemplate"));
const AdminAppTemplate = lazy(() => import("routes/AdminAppTemplate"));
const UserAppTemplate = lazy(() => import("routes/UserAppTemplate"));
const TrainerAppTemplate = lazy(() => import("routes/TrainerAppTemplate"));

let router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<ClientError />}>
        <Route path={RouteConstant.auth} element={<AuthPage />} />

        <Route path={RouteConstant.chooseProfile} element={
          <Suspense fallback={<LoginPreload />}>
            <ChooseProfilePage />
          </Suspense>
        } />

        <Route path={APP_ROUTE_PREFIX + '/*'} element={
          <Suspense fallback={<PreloadPage />}>
            <UserAppTemplate />
          </Suspense>
        } />
        
        <Route path={ADMIN_ROUTE_PREFIX + '/*'} element={
          <Suspense fallback={<PreloadPage />}>
            <AdminAppTemplate />
          </Suspense>
        } />

        <Route path={TRAINER_ROUTE_PREFIX + '/*'} element={
          <Suspense fallback={<PreloadPage />}>
            <TrainerAppTemplate />
          </Suspense>
        } />
        
        <Route path={SUPER_ADMIN_ROUTE_PREFIX + '/*'} element={
          <Suspense fallback={<PreloadPage />}>
            <SuperAdminAppTemplate />
          </Suspense>
        } />
        
        <Route path={TrainerRouteConstant.app.courses.trainer.printQr(':idBatch', ':idSchedule')} element={
          <Suspense fallback={<LoginPreload />}>
            <PrintQrCode />
          </Suspense>
        } />
        <Route path={RouteConstant.appRedirect} element={
          <Suspense fallback={<LoginPreload />}>
            <AppRedirect />
          </Suspense>
        }></Route>

        <Route
          path={RouteConstant.app.offlineCourseAutoScan(":idCourse", ":batch", ":idSchedule")}
          element={
            <Suspense fallback={<LoginPreload />}>
              <OfflineCourseScan />
            </Suspense>
          }
        />
    </Route>
  )
);

function App() {

  const { OfflineWarningToast } = useNetworkHook()

  return (
    <RollbarProvider instance={RollbarConfig} >
      <ErrorBoundary level="error" fallbackUI={()=> <ClientError />}>
        <OfflineWarningToast />
        <Provider>
            <RouterProvider router={router} />
        </Provider>
      </ErrorBoundary>
    </RollbarProvider>
  );
}

export default App;
